import { getConsultationsByClient } from '@/lib/api/consultations/consultation-api'
import { getExamsByClient } from '@/lib/api/exams/exam-api'
import { getOrdersByClient } from '@/lib/api/orders/order-api'

/**
 * Vendas em aberto do contato: atendimentos que já existem e esperam pagamento, ou que
 * já foram pagos e ainda esperam a data (ver docs/ai-user.md, "Fase 2, etapa 2b" e
 * "Fase 3, parte 2": sem eles a IA não tem o id para procurar a vaga).
 *
 * A IA só sabe o que está na conversa. Uma venda enviada sem a mensagem de confirmação
 * (descartada, falha no envio, aba fechada) não deixava rastro nenhum, e no passo seguinte
 * ela agendava tudo de novo: atendimento e cobrança em dobro. Esta lista entra no contexto
 * de cada passo e trava o envio de itens que já estão numa venda aberta.
 */

/** Uma venda: um atendimento, ou vários na mesma cobrança. Valores em centavos. */
export interface OpenSale {
    billingId: number | null
    /** Status da cobrança; `null` = agendado sem cobrança ("pagar depois"). */
    billingStatus: string | null
    appointments: Array<{
        kind: 'EXAM' | 'CONSULTATION' | 'PROCEDURE'
        id: number
        items: string[]
        catalogIds: number[]
        partner: string | null
        scheduledAt: string | null
    }>
    /** Uma linha por forma a pagar (a principal e as partes), com o link de cada uma. */
    payments: Array<{ method: string | null; amount: number; checkoutUrl: string | null; pixCopyPaste: string | null }>
}

/** Só olha o que é recente: venda antiga em aberto é assunto de cobrança, não de agendamento. */
const RECENT_DAYS = 30
const OPEN_BILLING_STATUSES = ['PENDING', 'OVERDUE', 'CREATED']
/** Venda paga ainda está "em aberto" enquanto algum atendimento dela não tem data. */
const PAID_BILLING_STATUSES = ['PAID', 'PARTIALLY_PAID']
const CLOSED_APPOINTMENT_STATUSES = ['CANCELLED', 'COMPLETED', 'DONE', 'NO_SHOW']
const CHECKOUT_LINK_METHODS = ['PIX', 'CREDIT_CARD', 'DEBIT_CARD', 'BOLETO']

function paymentOf(billing: any) {
    const method: string | null = billing?.paymentMethod ?? null
    return {
        method,
        amount: Number(billing?.amount ?? 0),
        checkoutUrl:
            billing?.publicToken && (!method || CHECKOUT_LINK_METHODS.includes(method))
                ? `${window.location.origin}/pay/${billing.publicToken}`
                : null,
        pixCopyPaste: billing?.pixCopyPaste ?? null,
    }
}

function itemsOf(kind: OpenSale['appointments'][number]['kind'], record: any): { names: string[]; ids: number[] } {
    // Exame guarda os itens em `items`; consulta e pedido têm o item principal no próprio
    // registro (`catalogId`), e o pedido pode ter mais em `items`.
    const lines: any[] =
        kind === 'EXAM'
            ? record.items ?? []
            : [
                  ...(record.catalogId ? [{ catalogId: record.catalogId, catalog: record.catalog }] : []),
                  ...((record.items ?? []) as any[]),
              ]
    const names = lines.map(line => String(line?.catalog?.name ?? line?.name ?? '')).filter(Boolean)
    const ids = lines
        .map(line => Number(line?.catalogId ?? line?.catalog?.id))
        .filter(id => Number.isInteger(id) && id > 0)
    if (names.length === 0 && record.title) names.push(String(record.title))
    return { names, ids }
}

export async function findOpenSales(clientId: number): Promise<OpenSale[]> {
    const [exams, consultations, orders] = await Promise.all([
        getExamsByClient(clientId),
        getConsultationsByClient(clientId),
        getOrdersByClient(clientId),
    ])
    const since = Date.now() - RECENT_DAYS * 24 * 60 * 60 * 1000
    const records: Array<{ kind: OpenSale['appointments'][number]['kind']; record: any }> = [
        ...((exams.success && exams.result) || []).map(record => ({ kind: 'EXAM' as const, record })),
        ...((consultations.success && consultations.result) || []).map(record => ({ kind: 'CONSULTATION' as const, record })),
        ...((orders.success && orders.result) || []).map(record => ({ kind: 'PROCEDURE' as const, record })),
    ]
    const byBilling = new Map<string, OpenSale>()
    for (const { kind, record } of records) {
        const created = new Date(record.createdAt ?? record.requestedAt ?? 0).getTime()
        if (!(created >= since)) continue
        if (CLOSED_APPOINTMENT_STATUSES.includes(String(record.status ?? '').toUpperCase())) continue
        if (record.isBudget === true || String(record.status ?? '').toUpperCase() === 'BUDGET') continue
        const billing = record.billing ?? null
        const paidWithoutDate = billing && PAID_BILLING_STATUSES.includes(String(billing.status)) && !record.scheduledAt
        if (billing && !OPEN_BILLING_STATUSES.includes(String(billing.status)) && !paidWithoutDate) continue
        const key = billing ? `b${billing.id}` : `${kind}${record.id}`
        const sale: OpenSale = byBilling.get(key) ?? {
            billingId: billing?.id ?? null,
            billingStatus: billing?.status ?? null,
            appointments: [],
            payments: billing && !paidWithoutDate
                ? [
                      paymentOf(billing),
                      ...((billing.adjustments ?? []) as any[])
                          .filter(child => OPEN_BILLING_STATUSES.includes(String(child?.status)))
                          .map(paymentOf),
                  ]
                : [],
        }
        const items = itemsOf(kind, record)
        sale.appointments.push({
            kind,
            id: record.id,
            items: items.names,
            catalogIds: items.ids,
            partner: record.partner?.name ?? record.partner?.tradingName ?? null,
            scheduledAt: record.scheduledAt ?? null,
        })
        byBilling.set(key, sale)
    }
    return [...byBilling.values()]
}

const KIND_LABEL = { EXAM: 'exame', CONSULTATION: 'consulta', PROCEDURE: 'procedimento' } as const

/** O bloco do contexto do modelo. Vazio quando não há venda aberta. */
export function describeOpenSales(sales: OpenSale[]): string {
    if (sales.length === 0) return ''
    const lines = sales.map(sale => {
        const appointments = sale.appointments
            .map(
                entry =>
                    `${KIND_LABEL[entry.kind]} #${entry.id} (${entry.items.join(', ') || 'sem itens'})` +
                    `${entry.partner ? ` em ${entry.partner}` : ''}, ${entry.scheduledAt ? `agendado para ${entry.scheduledAt}` : 'sem data'}`,
            )
            .join('; ')
        const payments = sale.billingStatus && PAID_BILLING_STATUSES.includes(sale.billingStatus)
            ? 'paga, falta a data'
            : sale.payments.length
            ? sale.payments
                  .map(
                      payment =>
                          `${payment.method ?? 'forma a definir'} R$ ${(payment.amount / 100).toFixed(2).replace('.', ',')}` +
                          (payment.checkoutUrl ? ` link ${payment.checkoutUrl}` : '') +
                          (payment.pixCopyPaste ? ` pix_code ${payment.pixCopyPaste}` : ''),
                  )
                  .join(' | ')
            : 'sem cobrança (pagar depois)'
        return `- ${appointments}. Cobrança ${sale.billingId ? `#${sale.billingId} (${sale.billingStatus})` : '-'}: ${payments}`
    })
    return [
        'VENDAS EM ABERTO DESTE CONTATO (já agendadas por alguém da equipe ou por você, aguardando pagamento ou a data):',
        ...lines,
        'Não agende de novo o que já está aqui. Se o contato perguntar como pagar, mande o link (e, no Pix, o ' +
            'pix_code) da venda aberta.',
    ].join('\n')
}
